// Mirrors PinterestHandle/PinterestProfileInput (PRI.Api) - kept in sync by hand since the web app
// has no shared code with the backend, same as lib/youtube.ts's relationship to its own C#
// counterparts. Both regexes are the actual security boundary here too: neither capture can admit
// a different host into the profile-status/RSS URLs built from them.
const USERNAME_PATTERN = /^\w{3,30}$/;
const LOCALE_PATTERN = /^[a-z]{2,3}$/;
const PROFILE_URL_PATTERN =
  /^(?:https?:\/\/)?(?:([a-z]{1,3})\.)?pinterest\.com\/(\w{1,40})(?:\/.*)?$/i;

// Board slugs are Pinterest's own percent-encoded, lowercased path segment - never derivable from
// a board's human-readable title (a real one: "%e5%92%8c%e7%94%b0...wada-azusa" for a board named
// "和田あずさ(Wada Azusa)") - so this only validates the character set of a slug that must be
// passed through verbatim from a real Pinterest URL, not construct one.
const BOARD_SLUG_PATTERN = /^[\d%a-z][\d%\-a-z]{0,78}[\d%a-z]$/;
const BOARD_URL_PATTERN =
  /^(?:https?:\/\/)?(?:[a-z]{1,3}\.)?pinterest\.com\/(?<handle>\w{1,40})\/(?<board>[\d%\-a-z]{1,80})(?:\/.*)?$/i;
const BARE_BOARD_PATH_PATTERN = /^(?<handle>\w{1,40})\/(?<board>[\d%\-a-z]{1,80})$/i;

// First path segments Pinterest reserves for its own routes rather than profiles - mirrors
// PinterestHandle.ReservedSegments. They pass USERNAME_PATTERN ("pin" is even a legal length), so
// without this a pasted pin URL parses as the profile "pin", or as board "{pinId}" owned by handle
// "pin". Pinterest reserves these itself, so no real handle collides with one.
const RESERVED_SEGMENTS = new Set([
  'pin',
  'pins',
  'search',
  'ideas',
  'today',
  'explore',
  'discover',
  'categories',
  'topics',
  'business',
  'settings',
  'login',
  'news_hub'
]);

const isValidHandle = (handle: string) =>
  USERNAME_PATTERN.test(handle) && !RESERVED_SEGMENTS.has(handle.toLowerCase());

export const DEFAULT_PINTEREST_LOCALE = 'www';

export type ParsedPinterestProfile = {username: string; locale: string};
export type ParsedPinterestBoard = {handle: string; board: string};

/**
 Accepts either a bare username or a full profile URL (with or without a locale subdomain, with
 or without a trailing path like /feed.rss); returns null for anything that isn't a syntactically
 valid Pinterest handle either way.
 */
export function parsePinterestProfileInput(raw: string): ParsedPinterestProfile | null {
  const trimmed = raw.trim();

  const urlMatch = PROFILE_URL_PATTERN.exec(trimmed);
  if (urlMatch) {
    const locale = (urlMatch[1] ?? DEFAULT_PINTEREST_LOCALE).toLowerCase();
    const username = urlMatch[2] ?? '';
    if (isValidHandle(username) && LOCALE_PATTERN.test(locale)) {
      return {username, locale};
    }

    return null;
  }

  if (isValidHandle(trimmed)) {
    return {username: trimmed, locale: DEFAULT_PINTEREST_LOCALE};
  }

  return null;
}

/**
 Accepts either a bare "{handle}/{board}" path or a full board URL (with or without a locale
 subdomain, with or without a trailing path/query); returns null for anything that isn't a
 syntactically valid Pinterest board reference either way.
 */
export function parsePinterestBoardInput(raw: string): ParsedPinterestBoard | null {
  const trimmed = raw.trim();

  const match = BOARD_URL_PATTERN.exec(trimmed) ?? BARE_BOARD_PATH_PATTERN.exec(trimmed);
  const handle = match?.groups?.handle;
  const board = match?.groups?.board?.toLowerCase();

  if (
    handle !== undefined &&
    board !== undefined &&
    isValidHandle(handle) &&
    BOARD_SLUG_PATTERN.test(board)
  ) {
    return {handle, board};
  }

  return null;
}

// A pin URL, or a bare pin id. Accepts any pinterest.com locale subdomain; the id is the only thing
// taken from a URL, so the lookup URL is never built from pasted text. pin.it short links are
// deliberately not resolved - they would need a redirect fetch to an attacker-chosen host.
//
// A bare number is ambiguous, since all-digit usernames are legal handles. Real pin ids are 17-19
// digits and a username is at most 30 characters, so only a bare number of 15+ digits is taken as a
// pin id; anything shorter stays a username (use the pin's URL for an older, shorter id).
const PIN_URL_PATTERN =
  /^(?:https?:\/\/)?(?:[a-z]{1,3}\.)?pinterest\.com\/pin\/(\d{1,25})(?:[#/?].*)?$/i;
const BARE_PIN_ID_PATTERN = /^\d{15,25}$/;

export function parsePinterestPinInput(raw: string): string | null {
  const trimmed = raw.trim();
  return PIN_URL_PATTERN.exec(trimmed)?.[1] ?? (BARE_PIN_ID_PATTERN.test(trimmed) ? trimmed : null);
}

/**
 The pin id an item stands for - the key an image-search match is recorded under (see the
 image-matches endpoint, whose video_id holds the pin id on Pinterest). From the pin URL, else the
 external id when that is all digits. Null for anything else (a profile item).
 */
export function extractPinId(url: string | null | undefined, externalId: string): string | null {
  const fromUrl = url ? /\/pin\/(\d{1,25})(?:[#/?]|$)/.exec(url)?.[1] : undefined;
  return fromUrl ?? (/^\d{6,25}$/.test(externalId) ? externalId : null);
}

/**
 Where an analyst can look at a profile item on Pinterest itself. The item's own URL when it is a
 https pinterest.com one (it is scraped, so anything else is ignored rather than linked), else built
 from the handle. Null when there is neither.
 */
export function pinterestProfileUrl(item: {
  url: string | null;
  author_handle: string | null;
}): string | null {
  if (item.url) {
    try {
      const {protocol, hostname} = new URL(item.url);
      if (protocol === 'https:' && /^(?:[a-z]{1,3}\.)?pinterest\.com$/i.test(hostname)) {
        return item.url;
      }
    } catch {
      // Not a URL - fall through to the handle.
    }
  }

  return item.author_handle
    ? `https://www.pinterest.com/${encodeURIComponent(item.author_handle)}/`
    : null;
}
